import { useState } from 'react';
import { ROLE_LABEL } from '@doit/shared';
import { ChevronDown, ChevronRight, ShieldCheck } from 'lucide-react';
import clsx from 'clsx';
import { Button, EmptyState, PageHeader, Spinner } from '../../components/ui';
import { Select } from '../../components/form';
import { Badge } from '../../components/badges';
import { useAudit, useAuditEntities } from '../../api/queries';
import type { AuditEntry } from '../../api/types';
import { formatDateTime } from '../../lib/format';

const ACTIONS = [
  'CREATE',
  'UPDATE',
  'DELETE',
  'LOGIN',
  'INVITE',
  'ACCEPT_INVITE',
  'MARK_ATTENDANCE',
  'SET_FLAG',
] as const;

const ACTION_LABEL: Record<string, string> = {
  CREATE: 'შექმნა',
  UPDATE: 'ცვლილება',
  DELETE: 'წაშლა',
  LOGIN: 'შესვლა',
  INVITE: 'მოწვევა',
  ACCEPT_INVITE: 'რეგისტრაცია',
  MARK_ATTENDANCE: 'დასწრება',
  SET_FLAG: 'რისკის სტატუსი',
};

const ACTION_TONE: Record<string, 'ok' | 'brand' | 'danger' | 'warn' | 'neutral'> = {
  CREATE: 'ok',
  UPDATE: 'brand',
  DELETE: 'danger',
  LOGIN: 'neutral',
  INVITE: 'warn',
  ACCEPT_INVITE: 'ok',
  MARK_ATTENDANCE: 'brand',
  SET_FLAG: 'warn',
};

const PAGE_SIZE = 50;

export function AuditPage() {
  const [action, setAction] = useState('');
  const [entity, setEntity] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);

  const { data: entities } = useAuditEntities();
  const { data, isLoading } = useAudit({
    action: action || undefined,
    entity: entity || undefined,
    from: from || undefined,
    to: to || undefined,
    page,
    pageSize: PAGE_SIZE,
  });

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  function change<T>(setter: (v: T) => void) {
    return (v: T) => {
      setter(v);
      setPage(1);
    };
  }

  return (
    <>
      <PageHeader
        title="აუდიტი"
        subtitle={isLoading ? 'იტვირთება…' : `${data?.total ?? 0} ჩანაწერი · ვინ, რა და როდის`}
      />

      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Select
          value={action}
          onChange={(e) => change(setAction)(e.target.value)}
          placeholder="ყველა მოქმედება"
          options={ACTIONS.map((a) => ({ value: a, label: ACTION_LABEL[a] ?? a }))}
        />
        <Select
          value={entity}
          onChange={(e) => change(setEntity)(e.target.value)}
          placeholder="ყველა ობიექტი"
          options={(entities ?? []).map((e) => ({
            value: e.entity,
            label: `${e.entity} (${e.count})`,
          }))}
        />
        <input
          type="date"
          value={from}
          onChange={(e) => change(setFrom)(e.target.value)}
          className="rounded-lg border border-line-strong bg-surface px-3 py-2.5 text-sm focus:border-brand-600 focus:outline-none focus:ring-3 focus:ring-brand-100"
        />
        <input
          type="date"
          value={to}
          onChange={(e) => change(setTo)(e.target.value)}
          className="rounded-lg border border-line-strong bg-surface px-3 py-2.5 text-sm focus:border-brand-600 focus:outline-none focus:ring-3 focus:ring-brand-100"
        />
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner className="size-6" />
        </div>
      ) : (data?.items ?? []).length === 0 ? (
        <div className="rounded-[10px] border border-line bg-surface shadow-xs">
          <EmptyState
            icon={<ShieldCheck className="size-8" />}
            title="ჩანაწერები ვერ მოიძებნა"
            description="შეცვალე ფილტრები."
          />
        </div>
      ) : (
        <div className="overflow-hidden rounded-[10px] border border-line bg-surface shadow-xs">
          <ul className="divide-y divide-line">
            {(data?.items ?? []).map((entry) => (
              <AuditRow key={entry.id} entry={entry} />
            ))}
          </ul>

          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-line px-4 py-3 text-[13px]">
              <span className="text-ink-mute">
                გვერდი {page} / {totalPages}
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  წინა
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  შემდეგი
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}

// ============================================================

function AuditRow({ entry }: { entry: AuditEntry }) {
  const [open, setOpen] = useState(false);
  const hasPayload = Boolean(entry.before || entry.after);

  return (
    <li>
      <button
        onClick={() => hasPayload && setOpen((v) => !v)}
        className={clsx(
          'flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-left transition',
          hasPayload && 'hover:bg-canvas/60',
        )}
      >
        <span className="w-4 shrink-0 text-ink-mute">
          {hasPayload ? (
            open ? (
              <ChevronDown className="size-3.5" />
            ) : (
              <ChevronRight className="size-3.5" />
            )
          ) : null}
        </span>

        <span className="tabular w-36 shrink-0 text-[12px] text-ink-mute">
          {formatDateTime(entry.createdAt)}
        </span>

        <Badge tone={ACTION_TONE[entry.action] ?? 'neutral'}>
          {ACTION_LABEL[entry.action] ?? entry.action}
        </Badge>

        <span className="w-40 shrink-0 truncate text-[13px] font-medium text-ink">
          {entry.entity}
        </span>

        <span className="min-w-0 flex-1 truncate text-[12px] text-ink-soft">
          {entry.actor ? (
            <>
              {entry.actor.fullName}
              <span className="ml-1.5 text-ink-mute">{ROLE_LABEL[entry.actor.role]}</span>
            </>
          ) : (
            <span className="text-ink-mute">სისტემა</span>
          )}
        </span>

        {entry.ip && <span className="tabular text-[11px] text-ink-mute">{entry.ip}</span>}
      </button>

      {open && hasPayload && (
        <div className="grid gap-3 border-t border-line bg-canvas/50 px-4 py-3 sm:grid-cols-2">
          <Payload label="მანამდე" value={entry.before} />
          <Payload label="შემდეგ" value={entry.after} />
        </div>
      )}
    </li>
  );
}

function Payload({ label, value }: { label: string; value: unknown }) {
  if (!value) return null;
  return (
    <div>
      <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-ink-mute">
        {label}
      </p>
      <pre className="max-h-48 overflow-auto rounded-lg border border-line bg-surface p-2.5 text-[11px] leading-relaxed text-ink-soft">
        {JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
}
